import { useEffect, useState } from "react";
import useGetBranchId from "shared/hooks/useGetBranchId";
import useFirestore from "shared/hooks/useFirestore";
import { QueryDocumentSnapshot } from "firebase/firestore";

const useHomeworkStudentListView = () => {
    const {getBranchId} = useGetBranchId();
    const {readFirestore, readFirestoreWithQuery} = useFirestore();
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [modal, setModal] = useState({
        title:"",
        content:"",
        onConfirm:() => {},
        onClose:() => {},
    });
    const [studentList, setStudentList] = useState<any>([]);

    useEffect(() => {
        getStudentList();
    }, [])

    const getStudentList = async() => {
        setIsLoading(true);
        setLoadingProgress(0);
        try {
            const branch_id = await getBranchId();
            
            // 지점 데이터를 한 번씩 읽고 메모리에서 배정을 연결한다.
            // 학생마다 배정 및 학급을 조회하면 학생 수에 따라 요청이 늘어난다.
            const [response, lessons, assignments] = await Promise.all([
                readFirestoreWithQuery("User/Auth/Student", {field:"branch_id", operator:"==", data:branch_id}),
                readFirestore(`Lesson/Branch/${branch_id}`),
                readFirestore(`Assign/${branch_id}/Lesson`),
            ]);
            for (const snapshot of [response, lessons, assignments]) {
                if (!Array.isArray(snapshot?.docs)) {
                    throw snapshot instanceof Error ? snapshot : new Error("Invalid student list response");
                }
            }
            if(response.empty){
                setModal({
                    title:"ERROR",
                    content:"학생이 존재하지 않습니다",
                    onConfirm:() => setIsOpenModal(false),
                    onClose:() => setIsOpenModal(false)
                })
                setIsOpenModal(true);
                return;
            }

            const doc_list = response.docs;
            // status가 active인 학생만 필터링
            const activeStudents = doc_list.filter((doc: QueryDocumentSnapshot) => {
                const data = doc.data();
                const status = data.status as string | undefined;
                return status === "active" || status === undefined;
            });

            if(activeStudents.length === 0){
                setModal({
                    title:"알림",
                    content:"활성 상태인 학생이 없습니다",
                    onConfirm:() => setIsOpenModal(false),
                    onClose:() => setIsOpenModal(false)
                })
                setIsOpenModal(true);
                return;
            }

            const lessonsById = new Map<string, Record<string, unknown>>(
                lessons.docs.map((doc: QueryDocumentSnapshot) => [doc.id, { id: doc.id, ...doc.data() }])
            );
            const lessonsByStudent = new Map<string, Record<string, unknown>[]>();
            assignments.docs.forEach((doc: QueryDocumentSnapshot) => {
                const lesson = lessonsById.get(doc.id);
                const students = doc.data().students;
                if (!lesson || !Array.isArray(students)) return;
                // array-contains 조회와 동일하게 중복된 학생 ID도 학급당 한 번만 연결한다.
                new Set(students).forEach(uid => {
                    if (typeof uid !== "string") return;
                    const studentLessons = lessonsByStudent.get(uid) ?? [];
                    studentLessons.push(lesson);
                    lessonsByStudent.set(uid, studentLessons);
                });
            });

            const list = activeStudents.flatMap((doc: QueryDocumentSnapshot) => {
                const studentLessons = lessonsByStudent.get(doc.id) ?? [{}];
                return studentLessons.map(lesson_data => ({
                    uid: doc.id,
                    student_data: { ...doc.data() },
                    lesson_data,
                }));
            });
            setLoadingProgress(100);
            setStudentList(list);
        } catch (error) {
            console.error('Error fetching student list:', error);
            setModal({
                title:"ERROR",
                content:"학생 목록을 가져오는 중 오류가 발생했습니다",
                onConfirm:() => setIsOpenModal(false),
                onClose:() => setIsOpenModal(false)
            });
            setIsOpenModal(true);
        } finally {
            setIsLoading(false);
            setLoadingProgress(0);
        }
    }

    const sortStudentList = (list: any[], order: 'asc' | 'desc') => {
        return [...list].sort((a, b) => {
            const comparison = a.student_data.name.localeCompare(b.student_data.name);
            return order === 'asc' ? comparison : -comparison;
        });
    }

    // 정렬 순서 변경 핸들러
    const toggleSortOrder = () => {
        const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newOrder);
        setStudentList((prev:any) => sortStudentList(prev, newOrder));
    }

    // 검색어 변경 핸들러
    const handleSearch = (keyword: string) => {
        setSearchKeyword(keyword);
    }

    // 필터링된 학급 리스트
    const filteredStudentList = studentList.filter((student:any) => 
        student.student_data.name.toLowerCase().includes(searchKeyword.toLowerCase())
    );

    return{
        isOpenModal,
        modal,
        toggleSortOrder,
        handleSearch,
        searchKeyword,
        studentList: filteredStudentList,
        sortOrder,
        isLoading,
        loadingProgress
    }
}

export default useHomeworkStudentListView;
